import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { message } from 'antd';
import { AxiosError } from 'axios';

import AssignmentAPI from '../../../api/AssignmentAPI';
import { checkForPermissions } from '../../../context/userReducer';
import showAppError from '../../../shared/error';
import { useAppSelector } from '../../../shared/hooks';
import {
  IAssignmentSubmission,
  ICourseAssignment,
  IObtainedMarksForQuestion,
  ISubmitQuestionAnswerRequest,
} from '../../../types/assignmentTypes';
import { UAMPermissions } from '../../../types/userTypes';
import { extractPlainTextAndCountCharacters } from './AssignmentHelpers';

interface ContextProps {
  flow: 'submit' | 'preview' | 'review';
  assignment: ICourseAssignment | null;
  submission: IAssignmentSubmission | null;
  assignmentToEndAt: string; // ISO string for assignment end time
  questionsMap: Record<string, IObtainedMarksForQuestion>;
  questionsMapByIndex: { [key: number]: IObtainedMarksForQuestion };
  currentIndex: number;
  setCurrentIndex: (i: number) => void;
  hasPrevQuestion: boolean;
  hasNextQuestion: boolean;
  isSaving: boolean;
  showReviewAndSubmit: boolean;
  setShowReviewAndSubmit: (v: boolean) => void;
  closeSubmitAssignmentModal: () => void;
  updateSubmission: (newSubmission: Partial<IAssignmentSubmission>) => void;
  updateQuestionsMap: (
    questionId: string,
    question: Partial<IObtainedMarksForQuestion>,
  ) => void;
  saveQuestionAnswer: (
    questionId: string,
    action: 'save' | 'submit' | 'clear',
    data: ISubmitQuestionAnswerRequest,
    saveToServer?: boolean,
  ) => Promise<void>;
  openReviewSubmit: () => void;
  submitAnswersAndEndAssignment: (autoSubmit?: boolean) => Promise<void>;
  goToPrevOrNextQuestion: (type: 'prev' | 'next') => void;
  goToNextUnattemptedQuestion: () => void;
  reset: () => void;
}

const AssignmentContext = createContext<ContextProps | null>(null);

export const useAssignmentContext = () => {
  const ctx = useContext(AssignmentContext);
  if (!ctx)
    throw new Error('AssignmentContext must be used within its provider');
  return ctx;
};

export interface AssignmentProviderProps {
  flow: 'submit' | 'preview' | 'review';
  assignment: ICourseAssignment | null;
  submission: IAssignmentSubmission | null;
  onProgress: (progress: number, duration: number) => void;
  children: React.ReactNode;
  isOpen: boolean;
  closeModal: () => void;
}

export const AssignmentProvider: React.FC<AssignmentProviderProps> = ({
  flow,
  assignment,
  submission: submissionState,
  onProgress,
  children,
  closeModal,
}) => {
  const {
    _id: assignmentId,
    creator: assignmentCreatorId,
    enableCountdown,
    countdown,
  } = assignment || {};

  const {
    id: userId,
    type: userType,
    userAccessPermissions,
  } = useAppSelector((state) => state.user);

  const [submission, setSubmission] = useState<IAssignmentSubmission | null>(
    submissionState,
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showReviewAndSubmit, setShowReviewAndSubmit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const hasCreatorAccess = useMemo(() => {
    return (
      assignmentCreatorId === userId ||
      userType === 'creator_completed' ||
      (userType === 'creator_restricted' &&
        checkForPermissions(
          [UAMPermissions.COURSE_ACCESS],
          userAccessPermissions,
        ))
    );
  }, [assignmentCreatorId, userId, userType, userAccessPermissions]);

  const {
    obtainedMarksForQuestions = [],
    startedAt,
    updatedAt,
    submittedAt,
  } = submission || {};

  // Calculate countdown timer:
  // Case 1: If submittedAt is present, no countdown as assignment is already submitted
  // Case 2: If updatedAt is not present, use startedAt for countdown timer meaning that this is a fresh new submission
  // Case 3: If updatedAt is present, calculate how much time is left from the updatedAt timestamp as this is a resumed submission
  const assignmentToEndAt = useMemo(() => {
    if (submittedAt || !enableCountdown || !countdown || !startedAt) {
      return '';
    }

    const startTime = new Date(startedAt);
    let endTime: Date;

    if (!updatedAt) {
      // New submission, use startedAt as start time
      endTime = new Date(startTime.getTime() + countdown * 1000);
    } else {
      // Resumed submission, use updatedAt as start time
      const updatedTime = new Date(updatedAt);
      endTime = new Date(updatedTime.getTime() + countdown * 1000);
    }

    return endTime.toISOString();
  }, [submittedAt, enableCountdown, countdown, startedAt, updatedAt]);

  const { hasPrevQuestion, hasNextQuestion } = useMemo(() => {
    return {
      hasPrevQuestion: currentIndex > 0,
      hasNextQuestion: currentIndex < obtainedMarksForQuestions.length - 1,
    };
  }, [currentIndex, obtainedMarksForQuestions.length]);

  const questionsMap = useMemo<{
    [key: string]: IObtainedMarksForQuestion;
  }>(() => {
    return obtainedMarksForQuestions.reduce(
      (acc, question) => ({
        ...acc,
        [question._id]: question,
      }),
      {} as { [key: string]: IObtainedMarksForQuestion },
    );
  }, [obtainedMarksForQuestions]);

  const questionsMapByIndex = useMemo<{
    [key: number]: IObtainedMarksForQuestion;
  }>(() => {
    return obtainedMarksForQuestions.reduce(
      (acc, question, index) => ({
        ...acc,
        [index]: question,
      }),
      {} as { [key: number]: IObtainedMarksForQuestion },
    );
  }, [obtainedMarksForQuestions]);

  const updateSubmission = useCallback(
    (newSubmission: Partial<IAssignmentSubmission>) => {
      setSubmission((prev) => (prev ? { ...prev, ...newSubmission } : prev));
    },
    [],
  );

  const updateQuestionsMap = useCallback(
    (questionId: string, question: Partial<IObtainedMarksForQuestion>) => {
      setSubmission((prev) => {
        if (!prev) return prev;

        const list = prev.obtainedMarksForQuestions.map((q) =>
          q._id === questionId ? { ...q, ...question } : q,
        );

        return {
          ...prev,
          obtainedMarksForQuestions: list,
        };
      });
    },
    [],
  );

  const closeSubmitAssignmentModal = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const openReviewSubmit = useCallback(() => {
    setShowReviewAndSubmit(true);
  }, []);

  const goToNextUnattemptedQuestion = useCallback(() => {
    const total = Object.keys(questionsMapByIndex).length;

    for (let offset = 1; offset < total; offset += 1) {
      const i = currentIndex + offset;

      // Stop if we've reached the end of the array
      if (i >= total) {
        break;
      }

      const attempt = { ...questionsMapByIndex[i] };

      const isUnattempted =
        !attempt ||
        (!attempt.selectedOptions?.length &&
          !attempt.subjectiveAnswer?.trim() &&
          !attempt.attachments?.length);

      if (isUnattempted) {
        setCurrentIndex(i);
        return;
      }
    }

    // If no unattempted question found in forward direction
    openReviewSubmit();
  }, [currentIndex, openReviewSubmit, questionsMapByIndex]);

  const goToPrevOrNextQuestion = useCallback(
    (type: 'prev' | 'next') => {
      setCurrentIndex((prev) => {
        const next = type === 'next' ? prev + 1 : prev - 1;
        return Math.min(
          Math.max(next, 0),
          obtainedMarksForQuestions.length - 1,
        );
      });
    },
    [obtainedMarksForQuestions.length],
  );

  const saveQuestionAnswer = useCallback(
    async (
      questionId: string,
      action: 'submit' | 'clear' | 'save',
      data: ISubmitQuestionAnswerRequest,
      saveToServer = true,
    ) => {
      if (!assignmentId || !questionId) return;

      const prevState = { ...(submission || {}) } as IAssignmentSubmission;

      if (action === 'submit') {
        setIsSaving(true);
      }

      updateSubmission({
        obtainedMarksForQuestions: obtainedMarksForQuestions.map((q) =>
          q._id === questionId ? { ...q, ...data } : q,
        ),
        lastAttemptedQuestionId: questionId,
      });

      // Do not proceed if the user has creator access (creator or UAM cannot submit answers), or
      // if saveToServer is false (e.g. when typing in subjective answer)
      if (hasCreatorAccess || !saveToServer) {
        setIsSaving(false);
        return;
      }

      // if the question is subjective, we need to check the word limit and if it is exceeded, show an error
      if (data.subjectiveAnswer) {
        // find the question from the questionsMap
        const question = questionsMap[questionId];
        if (question) {
          const { wordLimit } = question;
          if (wordLimit) {
            const { characterCount } = extractPlainTextAndCountCharacters(
              data.subjectiveAnswer,
            );
            const limit = Math.floor(wordLimit * 5);
            if (characterCount > limit) {
              showAppError('Character limit exceeded');
              setIsSaving(false);
              return;
            }
          }
        }
      }

      try {
        const resp = await AssignmentAPI.submitAssignmentAnswer(
          assignmentId,
          questionId,
          action,
          data,
        );
        if (resp.status === 200) {
          if (action !== 'clear') {
            updateSubmission(resp.data.result);
          }

          if (action === 'submit') {
            goToNextUnattemptedQuestion();
          }
        } else {
          showAppError(resp.data.message);

          setSubmission(prevState); // revert to previous state on error
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          showAppError(error.response?.data.message);
        }
        setSubmission(prevState); // revert to previous state on error
      } finally {
        setIsSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      questionsMap,
      assignmentId,
      hasCreatorAccess,
      obtainedMarksForQuestions,
      submission,
    ],
  );

  const submitAnswersAndEndAssignment = useCallback(
    async (autoSubmit = false) => {
      if (!assignmentId) return;

      setIsSaving(true);

      // Do not proceed if the user has creator access (creator or UAM cannot submit answers)
      // And close the submit assignment modal immediately
      if (hasCreatorAccess) {
        setIsSaving(false);
        closeSubmitAssignmentModal();
        return;
      }

      try {
        const resp = await AssignmentAPI.assignmentAction(assignmentId, 'end');
        if (resp.status === 200) {
          message.success('Assignment submitted successfully');

          if (onProgress) {
            onProgress(100, 100);
          }

          if (!autoSubmit) {
            closeSubmitAssignmentModal();
          }
        } else {
          showAppError(resp.data.message);
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          showAppError(error.response?.data.message);
        }
      } finally {
        setIsSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignmentId, closeSubmitAssignmentModal, hasCreatorAccess],
  );

  const reset = useCallback(() => {
    setSubmission(submissionState);
    setCurrentIndex(0);
    setShowReviewAndSubmit(false);
    setIsSaving(false);
  }, [submissionState]);

  const valuesToPass = useMemo(
    () => ({
      flow,
      assignment,
      submission,
      assignmentToEndAt,
      questionsMap,
      questionsMapByIndex,
      currentIndex,
      setCurrentIndex,
      hasPrevQuestion,
      hasNextQuestion,
      isSaving,
      showReviewAndSubmit,
      setShowReviewAndSubmit,
      closeSubmitAssignmentModal,
      updateSubmission,
      updateQuestionsMap,
      saveQuestionAnswer,
      openReviewSubmit,
      submitAnswersAndEndAssignment,
      goToNextUnattemptedQuestion,
      goToPrevOrNextQuestion,
      reset,
    }),
    [
      flow,
      assignment,
      submission,
      assignmentToEndAt,
      questionsMap,
      questionsMapByIndex,
      currentIndex,
      hasPrevQuestion,
      hasNextQuestion,
      isSaving,
      showReviewAndSubmit,
      closeSubmitAssignmentModal,
      updateSubmission,
      updateQuestionsMap,
      saveQuestionAnswer,
      openReviewSubmit,
      submitAnswersAndEndAssignment,
      goToNextUnattemptedQuestion,
      goToPrevOrNextQuestion,
      reset,
    ],
  );

  return (
    <AssignmentContext.Provider value={valuesToPass as ContextProps}>
      {children}
    </AssignmentContext.Provider>
  );
};
